import {
  Component,
  effect,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  viewChildren,
} from '@angular/core';
import { OcrOverlayComponent } from '../overlay/ocr-overlay.component';
import { ToolbarService } from '../../service/app-state/toolbar.service';
import { WorkflowService } from '../../service/app-state/workflow.service';
import { LassoSelectionDirective } from '../../directives/lasso-selection.directive';
import {
  CtDocument,
  DocumentPreviewComponent,
  ToolbarButtonComponent,
} from '@ps-gui/claims-terminal-shared';
import { PageNavigationService } from '../../service/app-state/page-navigation.service';
import { TableOverlayStoreService } from '../../service/overlay/table-overlay-store.service';
import { TableExtractionToolHintsComponent } from './table-extraction-tool-hints/table-extraction-tool-hints.component';
import { Tooltip } from 'primeng/tooltip';
import { CtBatchStoreService } from '../../service/app-state/ct-batch-store.service';
import { ConsolidationService } from '../../service/consolidation.service';

@Component({
  selector: 'ct-extraction-document-preview',
  templateUrl: 'ct2-document-preview.component.html',
  imports: [
    OcrOverlayComponent,
    LassoSelectionDirective,
    DocumentPreviewComponent,
    TableExtractionToolHintsComponent,
    ToolbarButtonComponent,
    Tooltip,
  ],
})
export class Ct2DocumentPreviewComponent implements OnInit {
  readonly singleOpenedDocument = input<CtDocument>();
  readonly documents = input.required<CtDocument[]>();
  readonly isPreviewListExpandedInitially = input.required<boolean>();
  readonly ensureSingleOverlay = input(false);
  readonly isConsolidation = input.required<boolean>();
  readonly isTableExtraction = input(false);
  readonly enableOverlay = input(true);

  readonly documentSelected = output<string>();

  protected readonly toolbarService = inject(ToolbarService);
  protected readonly ctBatchStoreService = inject(CtBatchStoreService);
  protected readonly workflowService = inject(WorkflowService);
  protected readonly pageNavigationService = inject(PageNavigationService);
  private readonly consolidationService = inject(ConsolidationService);
  protected readonly tableOverlayStoreService = inject(TableOverlayStoreService);

  private readonly ocrOverlayComponents = viewChildren<OcrOverlayComponent>('ocrOverlayComponent');
  private readonly documentPreview =
    viewChild.required<DocumentPreviewComponent>('documentPreview');

  protected readonly enableAutoUpdate = signal(false);
  protected readonly isFieldExtractionOverlayCollapsed = signal(false);
  protected readonly isShowAllDocumentsEnabled = signal(false);
  protected readonly sortedDocuments = signal<CtDocument[]>([]);
  protected readonly selectedDocumentId = signal<string | undefined>(undefined);

  toggleShowAllDocuments() {
    this.isShowAllDocumentsEnabled.set(!this.isShowAllDocumentsEnabled());
  }

  setIsFieldExtractionOverlayCollapsed(bool: boolean) {
    this.isFieldExtractionOverlayCollapsed.set(bool);
  }

  constructor() {
    effect(() => {
      const showAllDocuments = this.isShowAllDocumentsEnabled();

      const documents = showAllDocuments
        ? this.ctBatchStoreService.ctBatch.documents
        : this.documents();

      this.sortedDocuments.set(
        this.isConsolidation()
          ? this.consolidationService.sortDocumentsByConsolidationPriority(documents)
          : documents,
      );
    });
  }

  ngOnInit(): void {
    this.pageNavigationService.navigate$('back').subscribe(() => {
      const areAllTablesConfirmed = this.ocrOverlayComponents()
        .map((ocrOverlayComponent) => ocrOverlayComponent.tables())
        .flatMap((tableCollection) => tableCollection.map((table) => table.confirmed()))
        .every((bool) => bool);

      if (areAllTablesConfirmed) {
        this.workflowService.navigateToPrevView();
      }
    });
  }

  @HostListener('window:keyup', ['$event'])
  protected onKeyUp(event: KeyboardEvent) {
    if (event.code === 'Escape' && !this.doUnconfirmedTablesExist()) {
      this.toolbarService.setTableCreationEnabled(false);
    }
  }

  doUnconfirmedTablesExist() {
    return this.tableOverlayStoreService.getAllTables().some((table) => !table.confirmed());
  }

  protected toggleTableCreation() {
    this.toolbarService.setTableCreationEnabled(!this.toolbarService.isTableCreationEnabled());
  }

  protected onDocumentSelected(documentId: string) {
    this.selectedDocumentId.set(documentId);
    this.documentSelected.emit(documentId);
  }

  setSelectedPageId(selectedPageId: string) {
    this.documentPreview().selectPage(selectedPageId, true);
  }
}
